import { LoadingOutlined } from '@ant-design/icons';
import { Button, Progress, notification } from 'antd';
import imageCompression from 'browser-image-compression';
import { ChangeEvent, FC, ImgHTMLAttributes, useCallback, useEffect, useState } from 'react';
import { COMPRESS_FILE_TYPE, QUALITY } from 'src/constants/storage.constants';
import { AuditLogger } from 'src/utils/audit-logger';

import useUploadTask from 'src/hooks/useUploadTask';

import classes from './ImageUploader.module.scss';

import ImageEditor from '../ImageEditor/ImageEditor';

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type ImageUploaderProps = ImgHTMLAttributes<HTMLImageElement> & {
  /**
   * 이미지 path
   * ex) product-image/P12559
   * 앞부분에 REACT_APP_STORAGE_BUCKET_DIR에 해당하는 env값이 자동으로 붙으므로 실제로는
   * gu-files/product-image/P12559 로 저장된다.
   */
  path: string;
  size?: number;
  onDeleteImage?: () => Promise<void>;
};

const auditLogger = AuditLogger.getInstance();

/** 파일 타입을 검증하고, 크기가 초과하면 자동 압축하여 반환한다. */
const validateAndCompressFile = async (file: File): Promise<File> => {
  if (!VALID_IMAGE_TYPES.includes(file.type)) {
    throw new Error(`지원하지 않는 파일 형식입니다. 지원 형식: JPG, PNG (현재: ${file.type || '알 수 없음'})`);
  }

  if (file.size <= MAX_FILE_SIZE) return file;

  const compressed = await imageCompression(file, {
    maxSizeMB: 5,
    useWebWorker: true,
    fileType: COMPRESS_FILE_TYPE,
    initialQuality: QUALITY,
  });
  notification.info({
    message: '이미지 최적화 완료',
    description: `${(file.size / 1024 / 1024).toFixed(1)}MB → ${(compressed.size / 1024 / 1024).toFixed(1)}MB`,
  });
  return compressed as File;
};

const ImageUploader: FC<ImageUploaderProps> = ({ path, src, size = 60, onDeleteImage, ...props }) => {
  const { uploadFile, initUploadTask, uploadStatus, percent, error } = useUploadTask(path);
  const [file, setFile] = useState<File>();
  const [imageUrl, setImageUrl] = useState('');

  // file이 변경될 때 blob URL을 생성하고, 이전 URL을 revoke한다.
  useEffect(() => {
    if (!file) {
      setImageUrl('');
      return;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  /** input 이벤트로 얻은 파일을 처리한다. */
  const onInputChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      try {
        const result = await validateAndCompressFile(e.target.files[0]);
        setFile(result);
      } catch (err) {
        notification.error({
          message: '이미지 처리 실패',
          description: err instanceof Error ? err.message : '이미지를 처리하는 중 오류가 발생했습니다.',
        });
      }
    }
  }, []);

  const onDeleteInputFile = useCallback(() => {
    setFile(undefined);
  }, []);

  const onUploadInputFile = useCallback(async () => {
    if (file) {
      const result = await uploadFile(file);
      if (result) {
        setFile(undefined);
        notification.success({
          message: '이미지 업로드 성공',
        });
      } else {
        auditLogger.log({
          action: 'CREATE',
          entity: 'issue',
          entityId: path,
          summary: '이미지 업로드 실패',
          level: 'error',
        });
      }
    }
  }, [file, uploadFile, path]);

  useEffect(() => {
    if (error) {
      notification.error({
        message: '이미지 업로드 실패',
        description: error,
      });
    }
  }, [error]);

  useEffect(() => {
    initUploadTask();
  }, [initUploadTask, src]);

  return (
    <div className={classes.imageUploaderContainer} style={{ width: size, height: size }}>
      {uploadStatus === 'uploading' && (
        <div className={classes.uploadStatusBox}>
          <Progress type='circle' percent={percent} size={size * 0.7} />
        </div>
      )}
      {uploadStatus === 'error' && (
        <div className={classes.uploadStatusBox}>
          <Button size='small' onClick={initUploadTask} danger>
            Error
          </Button>
        </div>
      )}
      {uploadStatus === 'success' && (
        <div className={classes.uploadStatusBox}>
          <LoadingOutlined style={{ color: 'var(--blue400)' }} />
        </div>
      )}
      <ImageEditor
        imageUrl={imageUrl}
        onInputChange={onInputChange}
        onDeleteInputFile={onDeleteInputFile}
        onUploadInputFile={onUploadInputFile}
        onDeleteImage={onDeleteImage}
        src={src}
        {...props}
      />
    </div>
  );
};

export default ImageUploader;
